import { createSelector } from "reselect";
import { IRootReducer, JogsById } from "../../reducers";
import { Agency, Jog, JogId } from "../../reducers/states/agency";
import { useSelector } from "react-redux";
import { useMemo } from "react";

// SELECTORS

/**
 * Base selectors
 */
const selectAgencyState = (state: IRootReducer) =>
  state.agencyReducer;

const selectJogsById = createSelector(
  [selectAgencyState],
  agencyState => agencyState.jogsById
);

const selectAllJogIds = createSelector(
  [selectAgencyState],
  agencyState => agencyState.jogIds
);

/**
 * All JOGs (denormalized)
 * Recomputes only when jogsById or allJogIds change.
 */
// const selectJogs = createSelector(
//   [selectJogsById, selectAllJogIds],
//   (jogsById, ids): Jog[] => ids.map(id => jogsById[id])
// );

/**
 * All JOGs that can be set as a user's primary JOG (denormalized)
 * Recomputes only when jogsById or allJogIds change.
 */
const selectJogsForUsers = createSelector(
  [selectJogsById, selectAllJogIds],
  (jogsById, ids): Jog[] => {
    return ids.map(id => jogsById[id])
      .filter(a => a.isUserJog)
      .sort((a, b) => a.shortName < b.shortName ? -1 : 1);
  }
);

/**
 * All JOGs useable as a filter (e.g., for Dispatch Events and Incidents) (denormalized)
 * Recomputes only when jogsById or allJogIds change.
 */
const selectJogsForFilter = createSelector(
  [selectJogsById, selectAllJogIds],
  (jogsById, ids): Jog[] => {
    return ids.map(id => jogsById[id])
      .filter(a => a.isFilter)
      .sort((a, b) => a.shortName < b.shortName ? -1 : 1);
  }
);

/**
 * All JOGs an Incident can be associated with (denormalized)
 * Recomputes only when jogsById or allJogIds change.
 */
const selectJogsForIncidents = createSelector(
  [selectJogsById, selectAllJogIds],
  (jogsById, ids): Jog[] => {
    return ids.map(id => jogsById[id])
      .filter(j => j.isFilter && (!j.isGroupOnly || j.id === JogId.EasternWa))
      .sort((j1, j2) => j1.name > j2.name ? 1 : -1);
  }
);

/**
 * Single JOG by ID (selector factory)
 * Perfect for child components or isolated lookups.
 */
const makeSelectJogById = (id: string) => {
  return createSelector([selectJogsById], selectJogsById => selectJogsById[id]);
};

/**
 * Bulk lookup by array of IDs
 * Ideal for components that need many JOGs at once.
 */
const makeSelectJogsByIds = (ids: string[]) => {
  return createSelector([selectJogsById], jogsById =>
    ids.map(id => jogsById[id])
  );
};

// HOOKS

// todo: jcavner: not used. maybe delete
/**
 * Get ALL JOGs (denormalized array)
 * Best for bulk rendering.
 */
// export const useJogsArray = (): Jog[] => {
//   const selector = useSelector(selectJogs);
//   return selector;
// };

// todo: jcavner: Consider keeping this or making "jogsForUsers" part of the useJogs hook?
// only 1 use, but a funny find() used where jogs.get() could be used....
/**
 * All JOGs that can be set as a user's primary JOG (denormalized)
 * Best for bulk rendering.
 */
export const useJogsForUsers = (): Jog[] => {
  const selector = useSelector(selectJogsForUsers);
  return selector;
};

/**
 * Get ONE JOG by ID
 * Best used inside child components.
 */
export const useJog = (id: JogId | string | undefined): Jog | undefined => {
  // todo? Backwards compatible conversion in case value of id is an "Old ID" ?
  //id = getAgencyId(id); 

  const selector = useSelector(makeSelectJogById(id || ""));
  return selector;
};

// todo: jcavner: No uses. Maybe delete. useJogs().many() can be used instead.
/**
 * Get MULTIPLE agencies by an array of IDs
 * Best for bulk lookups when you have a list of IDs.
 */
// export const useJogsByIds = (ids: JogId[]): Jog[] => {
//   return useSelector(makeSelectJogsByIds(ids));
// };

export const useJogs = (): {
  byId: JogsById,
  forFiltering: Jog[],
  forIncidents: Jog[],
  get: (id: JogId | string | undefined) => Jog | undefined,
  getMany: (ids: JogId[] | string[]) => Jog[],
  getName: (jogId: JogId | string) => string,
  getNames: (jogIds: string | JogId[] | string[] | undefined, fullName?: boolean) => string[],
  getShortName: (jogId: JogId | string) => string,
  getAgencyJogs: (agency: Agency | undefined) => Jog[],
} => {
  const jogsById = useSelector(selectJogsById);
  const jogsForFiltering = useSelector(selectJogsForFilter); // todo: jcavner: by adding this, do I cause 2 calls to useEffect functions if state.jogsById changes? test it.
  const jogsForIncidents = useSelector(selectJogsForIncidents);

  // todo: console.warn if a jog is not found by the provided ID.
  return useMemo(() => {
    const get = (id: JogId | string | undefined): Jog | undefined => {
      return jogsById[id || ""];
    };

    const getShortName = (id: JogId | string): string => {
      return jogsById[id]?.shortName;
    };

    const getMany = (ids: JogId[] | string[]): Jog[] => {
      // todo: jcavner: filter out undefined
      return ids.map(id => jogsById[id]).sort((a, b) => a.shortName < b.shortName ? -1 : 1);
    };

    const getName = (jogId: JogId | string): string => {
      return jogsById[jogId]?.name;
    };

    const getNames = (jogIds: string | JogId[] | string[] | undefined, fullName = false): string[] => {
      if (!jogIds) return [];

      // if string, change to array
      if (typeof jogIds === "string") {
        jogIds = jogIds.split(",");
      }

      // get names
      let jogNames = jogIds.map((id) => {
        const jog = jogsById[id];
        if (!jog || id === JogId.Other) return "";
        return (fullName ? jog.name : jog.shortName) || "";
      });

      // Eleminate blanks and sort
      jogNames = jogNames?.filter((x) => !!x).sort();

      return jogNames;
    };

    // todo: jcavner: I'm not sure if I still need the reduce and jazz. agency.jogIds would ever have a value that doesn't return a value from jogsById
    // todo: jcavner: when I refactor useAgency into a similar hook with functions, it can have a reference to jogsByStore to provided its JOGs/this function, maybe
    const getAgencyJogs = (agency: Agency | undefined): Jog[] => {
      if (!agency) return [];
    
      // Use reduce to handle GetJog() possibly returning undefined
      const jogs = agency.jogIds?.reduce((jogs, id) => {
        let jog = jogsById[id];
        if(!jog) console.warn("No jog from jogsById for ID:", id, "Agency:", agency); // todo: jcavner: remove this debug log
        if(jog && jog.isUserJog) jogs.push(jog);
        return jogs;
      }, [] as Jog[]) || [];
    
      jogs.sort((j1, j2) => { return (j1.name > j2.name) ? 1 : -1; });
      
      return jogs;
    }

    return {
      byId: jogsById,
      forFiltering: jogsForFiltering,
      forIncidents: jogsForIncidents,
      get,
      getMany,
      getName,
      getNames,
      getShortName,
      getAgencyJogs
    };

  }, [jogsById, jogsForFiltering, jogsForIncidents]);
};